import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import API from '../../../../services/API'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

// 0 = indefinite access - accounts stay active until revoked or archived
const WINDOW_OPTIONS = [
  { value: 1, label: '1 Day' },
  { value: 3, label: '3 Days' },
  { value: 7, label: '1 Week' },
  { value: 14, label: '2 Weeks' },
  { value: 30, label: '1 Month' },
  { value: 90, label: '3 Months' },
  { value: 0, label: 'Indefinite - never expires' },
]

const SubcontractorSettings = () => {
  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  const [accessWindowDays, setAccessWindowDays] = useState(7)
  const [initialValue, setInitialValue] = useState(null)
  const [confirmIndefiniteOpen, setConfirmIndefiniteOpen] = useState(false)

  useEffect(() => {
    API.get('/settings?select=subcontractorSettings').then((res) => {
      if (res.status === 200) {
        const loaded = res.data?.subcontractorSettings?.accessWindowDays ?? 7
        setAccessWindowDays(loaded)
        setInitialValue(loaded)
      }
    })
  }, [])

  const save = () => {
    API.put('/site-config', {
      subcontractorSettings: { accessWindowDays },
    }).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Settings Updated' })
        setInitialValue(accessWindowDays)
        setOrgConfig((prev) => ({
          ...prev,
          orgSettings: {
            ...prev.orgSettings,
            subcontractorSettings: res.data?.subcontractorSettings,
          },
        }))
      } else {
        setToast({ severity: 'error', message: 'Unable to update settings' })
      }
    })
  }

  const handleSubmit = () => {
    if (initialValue !== null && accessWindowDays === initialValue) {
      setToast({ message: 'No changes made' })
      return
    }

    if (accessWindowDays === 0) {
      setConfirmIndefiniteOpen(true)
      return
    }

    save()
  }

  return (
    <Stack gap={3} padding={1}>
      <Typography variant="body2" color="text.secondary">
        Subcontractor accounts are activated for a limited access window when
        the subcontractor verifies their email or is reactivated. Once the
        window ends they can no longer log in until access is re-granted.
        Changing this setting does not shorten or extend windows that are
        already open.
      </Typography>

      <FormControl fullWidth>
        <InputLabel>Access Window</InputLabel>
        <Select
          value={accessWindowDays}
          onChange={(e) => setAccessWindowDays(e.target.value)}
          label="Access Window"
        >
          {WINDOW_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          How long should subcontractor accounts stay active after each
          activation?
        </FormHelperText>
      </FormControl>

      {accessWindowDays === 0 && (
        <Alert severity="warning">
          With indefinite access, subcontractor accounts never expire - they
          keep full access to the clinical and operational data their role
          allows until someone revokes access or archives the account.
          Forgotten accounts can permit unauthorised access. Your organisation
          is responsible for reviewing subcontractor accounts and removing
          access when each engagement ends.
        </Alert>
      )}

      <Button onClick={handleSubmit} variant="contained" color="success">
        Update
      </Button>

      <Dialog
        open={confirmIndefiniteOpen}
        onClose={() => setConfirmIndefiniteOpen(false)}
        fullWidth
      >
        <DialogTitle>Enable Indefinite Access?</DialogTitle>
        <DialogContent>
          <Typography>
            Subcontractor accounts will no longer expire automatically. Every
            future activation grants access that lasts until it is manually
            revoked or the account is archived. If accounts are not reviewed,
            former subcontractors may retain unauthorised access to patient
            and operational data. Are you sure?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmIndefiniteOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmIndefiniteOpen(false)
              save()
            }}
          >
            Enable Indefinite Access
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default SubcontractorSettings
